import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import { leadsKey } from './leads';
import type { OutreachInviter, OutreachTeam } from '../api/types';

/**
 * Force an outreach + clipping-platform status re-check for specific handles.
 *
 * Status now lives on the creator registry and arrives with the leads, so
 * there's no per-folder dedupe query on every render any more. This is the
 * explicit "I just exported / someone just claimed, show me now" path — the
 * background reconcile runs every 15 minutes, which is too slow to feel
 * responsive after a manual action.
 */
export function useRefreshStatuses(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (usernames: string[]) => client.registry.refreshStatus(usernames),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
      qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
    },
  });
}

export interface ExportInput {
  leadIds?: number[];
  niches?: string[];
  suggestedCategory?: string;
  assignToInviterId?: number;
  assignToTeam?: string;
}

export function useExportToOutreach(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: ExportInput) => client.folders.exportToOutreach(folderPublicId, input),
    onSuccess: () => {
      // The export endpoint writes the resulting status straight onto the
      // registry, so re-reading the leads is enough to flip the badges.
      qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
      qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
    },
  });
}

export function useOutreachInviters() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: ['discovery', 'outreach', 'inviters'],
    queryFn: () => client.outreach.listInviters(),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useOutreachTeams() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: ['discovery', 'outreach', 'teams'],
    queryFn: () => client.outreach.listTeams(),
    staleTime: 60_000,
    retry: 1,
  });
}
